import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { MeetingListResponse } from '@jabberr/core';
import { api, errorMessage } from '../lib/api.js';
import { duration, meetingTitle, shortDate } from '../lib/format.js';
import { TALK_TITLE, isTalk } from '../lib/plain.js';
import { useMeetingProject } from '../lib/team-projects.js';
import { useDrawer } from '../state/drawer.js';
import { useSession } from '../state/session.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorBox, Spinner } from '../components/ui.js';

export function MeetingsPage() {
  const { user, projects } = useSession();
  const [params] = useSearchParams();
  const teams = useMeetingProject(user?.id, projects, params.get('project'));
  const projectId = teams.selected?.id;
  const drawer = useDrawer();
  const navigate = useNavigate();
  const [result, setResult] = useState<{ projectId: string; meetings: MeetingListResponse['meetings'] } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { drawer.setInfo({ projectId, title: 'Meetings' }); }, [drawer.setInfo, projectId]);
  useEffect(() => {
    let alive = true;
    setResult(null); setError('');
    if (!projectId) return;
    const refresh = () => void api.meetings.list(projectId).then(({ meetings }) => {
      if (alive) { setResult({ projectId, meetings }); setError(''); }
    }).catch(err => { if (alive) setError(errorMessage(err)); });
    refresh();
    const timer = window.setInterval(refresh, 15_000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [projectId]);
  const create = async (talk: boolean) => {
    if (!projectId || busy) return;
    setBusy(true); setError('');
    try {
      const { meeting } = await api.meetings.create(projectId, talk ? { title: TALK_TITLE, listenMode: 'always' } : { listenMode: 'keen' });
      teams.choose(projectId);
      navigate(`/meet/${meeting.id}`, { state: { inviteJabberr: talk } });
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const meetings = result && result.projectId === projectId ? result.meetings : null;
  return <div className="page stack">
    <h1>Meetings</h1>
    <p className="muted">Calls and meetings use team projects. Project members can see their transcripts. My space keeps your private text conversations.</p>
    {teams.loading ? <Spinner label="Finding your recent team project" /> : !teams.selected ? <EmptyState title="No active team project is available. Ask a project owner to add you to one." /> : <>
      <label className="row">Team project <select aria-label="Meetings team project" value={projectId} onChange={event => teams.choose(event.target.value)}>
        {teams.projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
      </select></label>
      <div className="row">
        <button className="btn btn-primary" disabled={busy} onClick={() => void create(true)}>Talk with Jabberr</button>
        <button className="btn" disabled={busy} onClick={() => void create(false)}>Start a meeting</button>
        <Link className="btn" to={`/p/${projectId}/recordings`}>Record or upload</Link>
        <Link className="link-btn" to={`/p/${projectId}`}>Open project</Link>
      </div>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {!meetings && !error ? <Spinner label="Loading meetings" /> : meetings?.length === 0 ? <EmptyState title="No calls or meetings in this project yet." /> : <ul className="list">
        {[...(meetings ?? [])].sort((a, b) => (b.status === 'live' ? 1 : 0) - (a.status === 'live' ? 1 : 0) || b.startedAt.localeCompare(a.startedAt)).map(meeting => <li className="list-item" key={meeting.id}>
          <Link className="list-main" to={meeting.status === 'live' ? `/meet/${meeting.id}` : meeting.recordingId ? `/r/${meeting.recordingId}` : `/p/${projectId}`}>
            <span className="list-title">{meetingTitle(meeting.title, meeting.startedAt)}</span>
            <span className="muted small">{isTalk(meeting) ? 'Call' : 'Meeting'} · {shortDate(meeting.startedAt)}{duration(meeting.startedAt, meeting.endedAt) ? ` · ${duration(meeting.startedAt, meeting.endedAt)}` : ''} · {meeting.status === 'live' ? 'Live now' : meeting.recordingId ? 'Ended · Open transcript' : 'Ended'}</span>
          </Link>
        </li>)}
      </ul>}
    </>}
  </div>;
}
